<template>
  <div class="calendar">
    <div class="calendar-header-bar">
      <button @click="prevMonth">
        ←
      </button>
      <span class="month-label">{{ monthName }} {{ currentYear }}</span>
      <button @click="nextMonth">
        →
      </button>
    </div>

    <div class="calendar-header">
      <div
        v-for="day in weekdays"
        :key="day"
        class="day-header"
      >
        {{ day }}
      </div>
    </div>
    <div class="calendar-grid">
      <div
        v-for="(cell, index) in calendarCells"
        :key="index"
        class="calendar-cell"
        :class="{ 'empty': !cell.day }"
        @click="onDayClick(cell.day)"
      >
        <div
          v-if="cell.day"
          class="calendar-day"
          :style="fillStyle(cell.fill)"
          :class="{ 'today': isToday(cell.day) && isSelected(cell.day), 'selected': isSelected(cell.day) }"
        >
          <div class="day-number">
            {{ cell.day }}
          </div>
          <div class="fill-text">
            {{ cell.fill }}%
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'ScheduleCalendar',
    props: {
      fillMap: {
        type: Object,
        default: () => ({}),
      },
      currentDate: {
        type: Date,
        required: false, // ← Убери required: true
        default: null,
      },
    },
    data () {
      const today = new Date()
      return {
        currentMonth: today.getMonth(),
        currentYear: today.getFullYear(),
        weekdays: ['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС'],
        startOffset: 0,
        daysInMonth: 0,
        dayFillMap: {
          1: 100,
          2: 80,
          3: 40,
          4: 60,
          5: 0,
          6: 100,
          7: 20,
          8: 90,
          9: 100,
          10: 55,
          11: 0,
          12: 75,
          13: 30,
          14: 95,
          15: 100,
          16: 100,
          17: 60,
          18: 30,
          19: 25,
          20: 90,
          21: 100,
          22: 35,
          23: 70,
          24: 85,
          25: 0,
          26: 50,
          27: 100,
          28: 65,
          29: 10,
          30: 95,
        },
      }
    },
    computed: {
      calendarCells () {
        const cells = []
        for (let i = 0; i < this.startOffset; i++) {
          cells.push({})
        }
        for (let day = 1; day <= this.daysInMonth; day++) {
          cells.push({
            day,
            fill: this.dayFillMap[day] || 0,
          })
        }
        return cells
      },
      monthName() {
        return new Date(this.currentYear, this.currentMonth).toLocaleString('ru-RU', {
          month: 'long'
        });
      }
    },
    mounted () {
      this.recalculateCalendar()
    },
    methods: {
      recalculateCalendar() {
        const first = new Date(this.currentYear, this.currentMonth, 1);
        this.startOffset = (first.getDay() + 6) % 7;
        this.daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();

        // emit для загрузки новых данных при смене месяца
        this.$emit("month-change", {
          year: this.currentYear,
          month: this.currentMonth
        });
      },
      fillStyle (percent) {
        return {
          background: `linear-gradient(to top, #81c784 ${percent}%, #e0e0e0 ${percent}%)`,
        }
      },
      isSelected(day) {
        if (!this.currentDate) return false;

        return (
          this.currentDate.getDate() === day &&
          this.currentDate.getMonth() === this.currentMonth &&
          this.currentDate.getFullYear() === this.currentYear
        );
      },
      isToday(day) {
        const today = new Date();
        return (
          today.getDate() === day &&
          today.getMonth() === this.currentMonth &&
          today.getFullYear() === this.currentYear
        );
      },
      onDayClick(day) {
        const selected = new Date(this.currentYear, this.currentMonth, day);
        this.$emit("update:current-date", selected);
      },
      prevMonth() {
        if (this.currentMonth === 0) {
          this.currentMonth = 11;
          this.currentYear--;
        } else {
          this.currentMonth--;
        }
        this.recalculateCalendar();
      },
      nextMonth() {
        if (this.currentMonth === 11) {
          this.currentMonth = 0;
          this.currentYear++;
        } else {
          this.currentMonth++;
        }
        this.recalculateCalendar();
      },
    },
  }
</script>

<style scoped>
.calendar {
  max-width1: 380px;
  margin: 0 auto;
  font-family: sans-serif;

  border: 1px solid #e0e0e0;
  padding: 8px;
}

.calendar-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-weight: bold;
  padding: 4px 0;
}
.day-header {
  padding: 4px 0;
  background: #f0f0f0;
  border: 1px solid #ddd;
}
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}
.calendar-cell {
  aspect-ratio: 1 / 1;
}
.calendar-day {
  width: 100%;
  height: 100%;
  text-align: center;
  border: 1px solid #ccc;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  padding: 4px;
  box-sizing: border-box;
  transition: background 0.3s;

  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
}
.calendar-day:hover {
  background-color: #e0f2f1; /* мягкий голубовато-зелёный фон */
  transform: scale(1.02); /* лёгкое увеличение */
}

.empty {
  background: #fafafa;
}
.day-number {
  font-weight: bold;
}
.fill-text {
  font-size: 12px;
  color: #444;
}
.today {
  border: 2px solid #2196f3;
  box-shadow: 0 0 0 2px #bbdefb inset;
}


.calendar {
  max-width: 420px;
  font-family: sans-serif;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  background: white;
}

.calendar-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.month-label {
  font-weight: bold;
  font-size: 16px;
}

.calendar-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-weight: bold;
  padding-bottom: 4px;
}

.day-header {
  padding: 4px 0;
  background: #f0f0f0;
  border: 1px solid #ddd;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.calendar-cell {
  aspect-ratio: 1 / 1;
}

.calendar-day {
  width: 100%;
  height: 100%;
  text-align: center;
  border: 1px solid #ccc;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  padding: 4px;
  box-sizing: border-box;
  transition: background 0.3s, transform 0.2s;
  cursor: pointer;
}

.calendar-day:hover {
  background-color: #e0f2f1;
  transform: scale(1.02);
}

.empty {
  background: #fafafa;
}

.day-number {
  font-weight: bold;
}

.fill-text {
  font-size: 12px;
  color: #444;
}

.today {
  border: 2px solid #2196f3;
  box-shadow: 0 0 0 2px #bbdefb inset;
}

.selected {
  border: 2px solid #1a73e8;
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2) inset;
}
</style>
